/* The dynamic renderer — handover §14 step 9.
 *
 * One field definition in, one cell or one input out. Everything about a custom
 * record's shape is data, so this is the only place that knows what a 'money'
 * field looks like, and adding a field type means adding a case here rather
 * than touching a screen.
 *
 * The `person` and `supplier` types resolve against the real registers rather
 * than being free text, because a record pointing at "Dave" is worth less than
 * one pointing at a person who exists — and it is the same argument the rest of
 * the app makes about the account number.
 */

import type { FieldType, IPerson, ISupplier, PromotedColumn } from '../../models';
import { fmtDate, money } from '../../lib/format';
import { Badge } from '../ui';
import s from './screens.module.scss';

export interface FieldLookups {
  people: IPerson[];
  suppliers: ISupplier[];
  /* Servers, switches and the rest, and the applications that run on them.
     Optional because most forms have no use for either, and a form that had to
     be handed the whole device register to draw a text box would be a form
     nobody wants to add. */
  devices?: { id: string; label: string; group?: string }[];
  applications?: { id: string; label: string; group?: string }[];
}

/* What this renderer actually needs, which is less than a custom-record field.
   IRecordField satisfies it structurally, so the record types engine passes its
   definitions straight through — and the built-in registers can describe their
   own forms without borrowing a type that carries `promote` and `expiry`
   flags that mean nothing to them.

   `choices` is the one addition: a custom record's select stores the label it
   showed, where a built-in register may store an id and show something else —
   a person is a record id on the record and a name on screen. */
export interface RenderableField {
  id: string;
  label: string;
  type: FieldType;
  required?: boolean;
  /** Lifts `required` for the records it does not apply to. A shared mailbox has
   *  no depot, and a form that insists on one makes somebody invent an answer. */
  requiredUnless?: (values: Record<string, unknown>) => boolean;
  /** Options whose stored value is the same as the label shown. */
  options?: string[];
  /** Options whose stored value differs from the label shown. */
  choices?: { value: string; label: string }[];
  /** Offered, not enforced: a list to pick from on a field that still takes
   *  anything typed.
   *
   *  Most of what was being typed into this register has a usual answer and a
   *  long tail — makes of hardware, backup products, broadband types, what a
   *  site is for. A select would make the common case one click and the
   *  uncommon one impossible; free text made every case typing. This is the
   *  middle: the list drops down, and a value not on it is still a value.
   *
   *  Use `options`/`select` instead where the vocabulary really is closed and
   *  a value outside it would be a mistake — WPA2 or WPA3, and nothing else. */
  suggest?: string[];
  /** Where a `multi` field gets its options.
   *
   *  Naming the register rather than carrying the list, so the field definition
   *  stays data and the screen supplies what it already has loaded. */
  from?: 'devices' | 'applications';
  promote?: PromotedColumn;
  /** Shown under the control — units, format, or why the field matters. */
  hint?: string;
  /** Heading this field sits under. Consecutive fields sharing one are grouped;
      a list with none renders exactly as it always did. Seventeen inputs in a
      row is a wall, and the read panel next to it is in four labelled groups. */
  section?: string;
  /** When this field does not apply, given what else is on the form.
   *
   *  Seats in use is the case: typed for a per-device licence, counted from the
   *  holders for a per-user one — so on a per-user licence it is not merely
   *  unnecessary, it is a second answer that could disagree with the first.
   *
   *  Greyed rather than hidden. A field that vanishes leaves somebody wondering
   *  whether they imagined it; greyed, the form still says the option exists and
   *  the hint says what turns it on. */
  disabledWhen?: (values: Record<string, unknown>) => boolean;
  /** When this field does not exist for this record at all.
   *
   *  Different from disabledWhen, and the difference is whether the field is
   *  ever going to apply. Seats in use is greyed on a per-user licence because
   *  it is the same kind of thing and might apply tomorrow. Toner codes on a
   *  laptop are not a disabled field, they are not a field — and six greyed
   *  boxes on sixty-five devices is a form telling everybody about printers. */
  hiddenWhen?: (values: Record<string, unknown>) => boolean;
}

/** A value as it reads in a table cell or a detail row. */
export function renderValue(
  field: RenderableField,
  value: unknown,
  lookups: FieldLookups,
): React.ReactNode {
  if (value === undefined || value === null || value === '') {
    return <span className={s.cellSub}>—</span>;
  }

  switch (field.type) {
    case 'money':
      return money(Number(value));
    case 'number':
      return String(value);
    case 'date':
      return fmtDate(String(value));
    case 'check':
      return value ? <Badge tone="ok">Yes</Badge> : <Badge tone="muted">No</Badge>;
    case 'url':
      return (
        <a href={String(value)} target="_blank" rel="noreferrer">
          {String(value)}
        </a>
      );
    case 'person':
      /* Falls back to the raw value rather than rendering blank. A person who
         has left still has records pointing at them, and "—" would read as if
         the field had never been filled in. */
      return lookups.people.find((p) => p.id === value)?.name ?? String(value);
    case 'supplier':
      return lookups.suppliers.find((x) => x.id === value)?.name ?? String(value);
    case 'select': {
      const label = field.choices?.find((c) => c.value === value)?.label ?? String(value);
      return <Badge tone="muted">{label}</Badge>;
    }
    case 'multi': {
      const options = (field.from === 'devices' ? lookups.devices : lookups.applications) ?? [];
      const picked = Array.isArray(value) ? (value as string[]) : [];
      if (picked.length === 0) return <span className={s.cellSub}>—</span>;
      return picked.map((id) => options.find((o) => o.id === id)?.label ?? id).join(', ');
    }
    case 'textarea':
      return <span className={s.cellSub}>{String(value)}</span>;
    default:
      return String(value);
  }
}

/** The editing control for a field. */
export function FieldInput({
  field,
  value,
  onChange,
  lookups,
  values = {},
}: {
  field: RenderableField;
  value: unknown;
  onChange: (value: unknown) => void;
  lookups: FieldLookups;
  /** Everything else on the form, for fields that only apply sometimes. */
  values?: Record<string, unknown>;
}) {
  const id = `field-${field.id}`;
  const off = field.disabledWhen?.(values) ?? false;
  const label = (
    <label htmlFor={id}>
      {field.label}
      {field.required && !field.requiredUnless?.(values) && <span className={s.req}> *</span>}
      {field.promote && (
        <span className={s.hint} title={`Copied into the indexed ${field.promote} column on save`}>
          {' '}
          · promoted
        </span>
      )}
    </label>
  );

  if (field.type === 'check') {
    /* The checkbox carries its own label, so it does not get the block one —
       two labels for one control reads twice to a screen reader. */
    return (
      <div className={s.field}>
        <label htmlFor={id} className={s.checkRow}>
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={off}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{field.label}</span>
        </label>
      </div>
    );
  }

  /* Several of something, ticked rather than typed.
   *
   *  A backup job covering "HIRD-SV-001, the Syrinx database and the file
   *  shares" as free text is a sentence: it cannot be counted, cannot be joined
   *  back to the device it names, and quietly stops being true when a server is
   *  replaced. Ticked, the same job knows which records it covers, the device
   *  can say what backs it up, and a server nobody has ticked anywhere is a
   *  question the register can answer.
   *
   *  Checkboxes rather than a multi-select: a native one needs ctrl-click to add
   *  and loses the lot on a stray click, which is a cruel way to lose eight
   *  ticks. */
  if (field.type === 'multi') {
    const options = (field.from === 'devices' ? lookups.devices : lookups.applications) ?? [];
    const picked = new Set(Array.isArray(value) ? (value as string[]) : []);

    const toggle = (id: string) => {
      const next = new Set(picked);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange([...next]);
    };

    return (
      <div className={s.field}>
        {label}
        {options.length === 0 ? (
          <div className={s.hint}>Nothing in that register yet.</div>
        ) : (
          <div className={s.checkList}>
            {options.map((o) => (
              <label key={o.id} className={s.checkRow}>
                <input
                  type="checkbox"
                  checked={picked.has(o.id)}
                  disabled={off}
                  onChange={() => toggle(o.id)}
                />
                <span>
                  {o.label}
                  {o.group && <span className={s.cellSub}> · {o.group}</span>}
                </span>
              </label>
            ))}
          </div>
        )}
        {field.hint && <div className={s.hint}>{field.hint}</div>}
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className={s.field}>
        {label}
        <textarea
          id={id}
          value={String(value ?? '')}
          disabled={off}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === 'select' || field.type === 'person' || field.type === 'supplier') {
    const options =
      field.type === 'select'
        ? (field.choices ?? (field.options ?? []).map((o) => ({ value: o, label: o })))
        : field.type === 'person'
          ? lookups.people
              .filter((p) => p.status !== 'Leaver')
              .map((p) => ({ value: p.id, label: p.name }))
          : lookups.suppliers.map((x) => ({ value: x.id, label: x.name }));

    /* A value that is no longer on offer — a leaver, or an option removed from
       the type — is added back so opening a record does not silently rewrite it
       to the first entry in the list. */
    const current = String(value ?? '');
    if (current && !options.some((o) => o.value === current)) {
      options.unshift({ value: current, label: `${current} (no longer listed)` });
    }

    return (
      <div className={s.field}>
        {label}
        <select id={id} value={current} disabled={off} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {field.hint && <div className={s.hint}>{field.hint}</div>}
      </div>
    );
  }

  const type =
    field.type === 'date' ? 'date' : field.type === 'number' || field.type === 'money' ? 'number' : 'text';

  /* Only when there is something to offer. An empty datalist renders a control
     that opens onto nothing, which is worse than no control at all. */
  const listId = field.suggest && field.suggest.length > 0 ? `${id}-suggest` : undefined;

  return (
    <div className={s.field}>
      {label}
      <input
        id={id}
        list={listId}
        type={type}
        /* Money is entered in pounds and pence. A step of 1 would let the
           browser's spinner round somebody's 18.10 to 18. */
        step={field.type === 'money' ? '0.01' : undefined}
        value={String(value ?? '')}
        disabled={off}
        onChange={(e) =>
          onChange(
            field.type === 'number' || field.type === 'money'
              ? e.target.value === ''
                ? ''
                : Number(e.target.value)
              : e.target.value,
          )
        }
      />
      {listId && (
        <datalist id={listId}>
          {field.suggest!.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
      {field.hint && <div className={s.hint}>{field.hint}</div>}
    </div>
  );
}

/** Required fields with nothing in them. The save button reads this. */
export function missingRequired(
  fields: RenderableField[],
  payload: Record<string, unknown>,
): RenderableField[] {
  return fields.filter((f) => {
    if (!f.required || f.requiredUnless?.(payload)) return false;
    const v = payload[f.id];
    return v === undefined || v === null || v === '';
  });
}
